import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, TrendingUp, Clock, Package, Inbox } from "lucide-react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtNum } from "@/lib/constants";
import RatesAdmin from "@/components/admin/RatesAdmin";
import LinesAdmin from "@/components/admin/LinesAdmin";
import ChargesAdmin from "@/components/admin/ChargesAdmin";
import PortsAdmin from "@/components/admin/PortsAdmin";
import ExchangeAdmin from "@/components/admin/ExchangeAdmin";

const STATUSES = ["new", "in_progress", "quoted", "closed"];

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/rates/expiry-summary").then(({ data }) => setS(data)); }, []);
  const cards = [
    { label: "Total Rates", value: s?.total, icon: Package, color: "text-navy" },
    { label: "Active Rates", value: s?.active, icon: TrendingUp, color: "text-emerald-600" },
    { label: "Expiring in 7 days", value: s?.expiring_7_days, icon: Clock, color: "text-amber-600" },
    { label: "Expiring in 3 days", value: s?.expiring_3_days, icon: AlertTriangle, color: "text-signal" },
    { label: "Expired", value: s?.expired, icon: AlertTriangle, color: "text-destructive" },
    { label: "Upcoming", value: s?.upcoming, icon: Clock, color: "text-cyan-700" },
  ];
  return (
    <div>
      {s && (s.expiring_3_days > 0 || s.expiring_7_days > 0) && (
        <div className="flex items-center gap-2 bg-signal/10 border border-signal/30 rounded-sm px-4 py-3 mb-6 text-sm text-navy" data-testid="expiry-alert">
          <AlertTriangle className="h-4 w-4 text-signal" />
          {s.expiring_3_days} rate(s) expire within 3 days · {s.expiring_7_days} within 7 days. Review and renew.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border rounded-md overflow-hidden">
        {cards.map((c) => (
          <div key={c.label} className="bg-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon className={`h-5 w-5 ${c.color}`} />
            <div className="font-mono-data font-bold text-3xl text-navy mt-3">{c.value ?? "-"}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/enquiries").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/enquiries/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-muted-foreground text-sm">No enquiries received yet.</p>}
      {items.map((e) => (
        <div key={e.id} className="border border-border rounded-md p-5 bg-card" data-testid={`admin-enq-${e.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="font-heading font-bold text-navy">{e.company_name} — {e.pol} → {e.pod}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono-data">
                {e.contact_person} · {e.email} · {e.phone}
              </div>
              <div className="text-xs text-muted-foreground mt-1 capitalize">
                {e.shipment_type} · {e.shipping_line || "Any"} · {e.num_containers}× {e.container_size || e.container_type}
                {e.hazardous ? " · Hazardous" : ""} {e.cargo_description ? `· ${e.cargo_description}` : ""}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="text-xs font-mono-data text-muted-foreground">{(e.created_at || "").slice(0, 10)}</span>
              <Select value={e.status || "new"} onValueChange={(v) => setStatus(e.id, v)}>
                <SelectTrigger className="h-8 w-36 rounded-sm capitalize" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {e.selected_rate && (
            <div className="mt-3 pt-3 border-t border-border text-sm font-mono-data text-navy">
              Selected: {e.selected_rate.shipping_line} · {e.selected_rate.currency} {fmtNum(e.selected_rate.ocean_freight)} · {e.selected_rate.size_label}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Bookings() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/bookings").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/bookings/${id}/status`, { status });
    toast.success("Booking status updated");
    load();
  };
  const STATUSES2 = ["new", "confirmed", "cancelled"];
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-muted-foreground text-sm">No bookings received yet.</p>}
      {items.map((b) => (
        <div key={b.id} className="border border-border rounded-md p-5 bg-card" data-testid={`admin-booking-${b.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              {b.booking_ref && <div className="font-mono-data text-xs font-bold text-signal mb-1" data-testid={`admin-booking-ref-${b.id}`}>{b.booking_ref}</div>}
              <div className="font-heading font-bold text-navy">{b.company_name} — {b.pol} → {b.pod}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono-data">{b.contact_person} · {b.email} · {b.phone}</div>
              <div className="text-xs text-muted-foreground mt-1 capitalize">
                {b.shipment_type} · {b.shipping_line} · {b.num_containers}× {b.container_size}
                {b.cargo_description ? ` · ${b.cargo_description}` : ""}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="text-xs font-mono-data text-muted-foreground">{(b.created_at || "").slice(0, 10)}</span>
              <Select value={b.status || "new"} onValueChange={(v) => setStatus(b.id, v)}>
                <SelectTrigger className="h-8 w-36 rounded-sm capitalize" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES2.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {b.rate_snapshot && (
            <div className="mt-3 pt-3 border-t border-border text-sm font-mono-data text-navy">
              {b.rate_snapshot.currency} {fmtNum(b.rate_snapshot.ocean_freight)} + INR {fmtNum(b.rate_snapshot.local_charges_inr)} local · {b.rate_snapshot.size_label} · valid {b.rate_snapshot.valid_until}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ChangeLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/rate-audit").then(({ data }) => setItems(data)); }, []);
  const color = { create: "text-emerald-600", update: "text-amber-600", duplicate: "text-cyan-700", delete: "text-destructive" };
  return (
    <div className="border border-border rounded-md overflow-hidden bg-card">
      {items.length === 0 && <p className="text-muted-foreground text-sm p-5">No rate changes logged yet. Add, edit, duplicate or delete a rate to see the audit trail.</p>}
      {items.map((a) => (
        <div key={a.id} className="flex items-start gap-4 px-5 py-3 border-b border-border last:border-0" data-testid={`audit-${a.id}`}>
          <span className={`font-mono-data text-xs uppercase font-bold w-20 shrink-0 ${color[a.action] || "text-navy"}`}>{a.action}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-navy break-words">{a.detail}</div>
            <div className="text-xs text-muted-foreground font-mono-data mt-0.5">{a.user_email} · {(a.timestamp || "").slice(0, 19).replace("T", " ")} UTC</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {  return (
    <Layout>
      <div className="bg-navy text-white py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <p className="text-signal font-mono-data text-xs">ADMIN CONTROL</p>
          <h1 className="font-heading font-extrabold text-3xl mt-1">Rate Management Dashboard</h1>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="overview">
          <div className="overflow-x-auto">
            <TabsList className="rounded-sm">
              <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="rates" data-testid="admin-tab-rates">Ocean Rates</TabsTrigger>
              <TabsTrigger value="charges" data-testid="admin-tab-charges">Local Charges</TabsTrigger>
              <TabsTrigger value="lines" data-testid="admin-tab-lines">Shipping Lines</TabsTrigger>
              <TabsTrigger value="ports" data-testid="admin-tab-ports">Ports</TabsTrigger>
              <TabsTrigger value="fx" data-testid="admin-tab-fx">Exchange Rates</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
              <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
              <TabsTrigger value="audit" data-testid="admin-tab-audit">Change Log</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
          <TabsContent value="rates" className="mt-6"><RatesAdmin /></TabsContent>
          <TabsContent value="charges" className="mt-6"><ChargesAdmin /></TabsContent>
          <TabsContent value="lines" className="mt-6"><LinesAdmin /></TabsContent>
          <TabsContent value="ports" className="mt-6"><PortsAdmin /></TabsContent>
          <TabsContent value="fx" className="mt-6"><ExchangeAdmin /></TabsContent>
          <TabsContent value="bookings" className="mt-6"><Bookings /></TabsContent>
          <TabsContent value="enquiries" className="mt-6"><Enquiries /></TabsContent>
          <TabsContent value="audit" className="mt-6"><ChangeLog /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
